import { useState } from "react";
import { ShieldCheck, StickyNote } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { OPS } from "@/constants/testIds";
import { ChipRow } from "./CoBranchSelect";
import { CO_TIMEOUT_MS, coMessage, fmt, fmtDateTime, labelOf } from "./coUtils";

const P = OPS.co.panel;
const box = "space-y-2 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] p-3";

function usePatchReview(rec, onSaved, okKey) {
    const { t, lang } = useI18n();
    const [busy, setBusy] = useState(false);
    const run = async (body) => {
        setBusy(true);
        try {
            const r = await api.patch(`/co/records/${rec.id}/review`, body, { timeout: CO_TIMEOUT_MS });
            toast.success(t(okKey));
            onSaved(r.data);
        } catch (e) {
            toast.error(coMessage(e, lang) || t("co_offline_error"));
        } finally {
            setBusy(false);
        }
    };
    return [busy, run];
}

/** `manager_note` — the branch manager's field (also co_review / master). */
export function ManagerNote({ rec, scope, onSaved }) {
    const { t } = useI18n();
    const [note, setNote] = useState(rec.review?.manager_note || "");
    const [busy, run] = usePatchReview(rec, onSaved, "co_rec_note_saved");
    const caps = scope?.caps || {};
    if (!(caps.can_manage_branch || caps.can_review || caps.is_master)) return null;
    return (
        <section className={box}>
            <h4 className="flex items-center gap-1.5 text-xs font-bold text-[color:var(--ink-muted)]">
                <StickyNote className="h-3.5 w-3.5" />{t("co_rec_manager_note")}
            </h4>
            <Textarea data-testid={P.managerNote} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder={t("co_rec_manager_note_ph")} className="bg-[color:var(--bg-surface)]" />
            <Button size="sm" variant="outline" data-testid={P.managerNoteSave} disabled={busy || note === (rec.review?.manager_note || "")}
                    onClick={() => run({ manager_note: note })}>
                {t("co_rec_save_note")}
            </Button>
        </section>
    );
}

/** The review action, on the rules already built: co_review / master (or a branch manager when
 *  `branch_manager_reviews` is on); re-opening to «يحتاج مراجعة» is co_review / master only;
 *  self-review is refused by the server (`CO_SELF_REVIEW`). */
export function ReviewActions({ rec, scope, onSaved }) {
    const { t, lang } = useI18n();
    const cur = rec.review?.status || null;
    const [status, setStatus] = useState(cur);
    const [note, setNote] = useState(rec.review?.note || "");
    const [busy, run] = usePatchReview(rec, onSaved, "co_rec_review_saved");
    const caps = scope?.caps || {};
    const may = caps.can_review || caps.is_master || (caps.can_manage_branch && scope?.settings?.branch_manager_reviews);
    if (!may) return null;
    const canReopen = caps.can_review || caps.is_master;
    const items = (scope?.labels?.review_statuses || []).filter((s) => s.key !== "pending" || canReopen);
    const dirty = status !== cur || note !== (rec.review?.note || "");
    const body = () => ({ ...(status !== cur && status ? { status } : {}), ...(note !== (rec.review?.note || "") ? { note } : {}) });
    return (
        <section data-testid={P.review} className={box}>
            <h4 className="flex items-center gap-1.5 text-xs font-bold text-[color:var(--ink-muted)]">
                <ShieldCheck className="h-3.5 w-3.5" />{t("co_rec_review_title")}
            </h4>
            <p data-testid={P.reviewCurrent} data-status={cur || "none"} className="text-[11px] text-[color:var(--ink-muted)]">
                {cur ? labelOf(scope?.labels?.review_statuses, cur, lang) : t("co_rec_review_none_yet")}
                {rec.review?.reviewed_at ? ` · ${fmt(t("co_rec_review_by"), { name: rec.review.reviewed_by_name || "", at: fmtDateTime(rec.review.reviewed_at, lang) })}` : ""}
            </p>
            <ChipRow items={items} value={status} onChange={setStatus} testId={P.reviewStatus} lang={lang} disabled={busy} />
            <Textarea data-testid={P.reviewNote} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder={t("co_rec_review_note_ph")} className="bg-[color:var(--bg-surface)]" />
            <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" data-testid={P.reviewSave} disabled={busy || !dirty} onClick={() => run(body())}>
                    {t("co_rec_review_save")}
                </Button>
                {rec.duplicate_of && !rec.duplicate_dismissed ? (
                    <Button size="sm" variant="outline" data-testid={P.dismissDuplicate} disabled={busy}
                            onClick={() => run({ dismiss_duplicate: true })}>
                        {t("co_rec_dismiss_duplicate")}
                    </Button>
                ) : null}
            </div>
        </section>
    );
}
